import type { RtkErrorShape } from "~/helpers/rtkErrorClassification";

import { extractGraphqlErrorCode } from "~/services/graphqlErrorHelpers";

export type UnauthorizedRetryAction = "redirectToLogin" | "replayRequest" | "surfaceNotReplayed";

export interface UnauthorizedRetryContext {
	endpointName?: string;
	operationType: "query" | "mutation";
	tokenRefreshed: boolean;
}

export type UnauthorizedOutcome<TResult> =
	| { action: "redirectToLogin" }
	| { action: "surfaceNotReplayed"; notReplayedError: unknown }
	| { action: "replayRequest"; replayed: TResult };

export interface UnauthorizedEffects<TResult> {
	redirectToLogin: () => void;
	replay: () => Promise<TResult>;
}

export const MUTATION_NOT_REPLAYED_AFTER_REFRESH = "MUTATION_NOT_REPLAYED_AFTER_REFRESH";

export const MUTATION_NOT_REPLAYED_MESSAGE =
	"Your session was renewed, so this change was not sent again. Please try again.";

// wsapi dedupes an add on the client line id and rewrites a line edit absolutely; a replayed removal is refused as LineNumberNotFound.
export const REPLAY_SAFE_MUTATION_ENDPOINTS: ReadonlySet<string> = new Set([
	"AddLineItems",
	"AddLineItemsWithPricing",
	"UpdateLineItem"
]);

export const isReplaySafeMutationEndpoint = (endpointName: string | undefined): boolean =>
	endpointName !== undefined && REPLAY_SAFE_MUTATION_ENDPOINTS.has(endpointName);

// wsapi can commit a mutation before its 401 surfaces, so re-sending it duplicates the write.
const resolveUnauthorizedRetryAction = ({
	endpointName,
	operationType,
	tokenRefreshed
}: UnauthorizedRetryContext): UnauthorizedRetryAction => {
	if (!tokenRefreshed) return "redirectToLogin";
	if (operationType === "query") return "replayRequest";
	return isReplaySafeMutationEndpoint(endpointName) ? "replayRequest" : "surfaceNotReplayed";
};

const NOT_REPLAYED_ERROR_NAME = "MutationNotReplayedAfterRefreshError";

const buildNotReplayedResponse = (): NonNullable<RtkErrorShape["response"]> => ({
	errors: [
		{
			extensions: { code: MUTATION_NOT_REPLAYED_AFTER_REFRESH },
			message: MUTATION_NOT_REPLAYED_MESSAGE
		}
	],
	status: 401
});

// RTK Query keeps this on the error it puts in Redux, so it stays a plain serializable object.
const buildMutationNotReplayedError = (): RtkErrorShape => ({
	message: MUTATION_NOT_REPLAYED_MESSAGE,
	name: NOT_REPLAYED_ERROR_NAME,
	response: buildNotReplayedResponse()
});

export class MutationNotReplayedAfterRefreshError extends Error {
	readonly response: NonNullable<RtkErrorShape["response"]>;

	constructor() {
		super(MUTATION_NOT_REPLAYED_MESSAGE);
		this.name = NOT_REPLAYED_ERROR_NAME;
		this.response = buildNotReplayedResponse();
	}
}

const isMutationNotReplayedError = (error: unknown): boolean =>
	extractGraphqlErrorCode(error) === MUTATION_NOT_REPLAYED_AFTER_REFRESH;

const resolveUnauthorized = async <TResult>(
	context: UnauthorizedRetryContext,
	effects: UnauthorizedEffects<TResult>
): Promise<UnauthorizedOutcome<TResult>> => {
	const action: UnauthorizedRetryAction = resolveUnauthorizedRetryAction(context);

	if (action === "redirectToLogin") {
		effects.redirectToLogin();
		return { action };
	}

	if (action === "surfaceNotReplayed") {
		return { action, notReplayedError: buildMutationNotReplayedError() };
	}

	return { action, replayed: await effects.replay() };
};

const unauthorizedRetryPolicy = {
	buildMutationNotReplayedError,
	isMutationNotReplayedError,
	resolveUnauthorized,
	resolveUnauthorizedRetryAction
};

export default unauthorizedRetryPolicy;
